import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSignIn, useUser } from '@clerk/react';
import { useAuth } from '../context/AuthContext';
import { Eye, EyeOff, Mail, Lock, Loader2, Car } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

export default function LoginPage() {
  const navigate = useNavigate();
  const { signIn, errors: clerkErrors, fetchStatus } = useSignIn();
  const { isLoaded: userLoaded, isSignedIn } = useUser();
  const { user } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [step, setStep] = useState('credentials'); // 'credentials', 'verification', 'forgot', 'reset-code', 'new-password'
  const [resendTimer, setResendTimer] = useState(0);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  // Redirect if already signed in
  useEffect(() => {
    if (isSignedIn && user && user.role !== 'pending') {
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'business') {
        navigate('/dashboard/overview');
      } else {
        navigate('/dashboard');
      }
    } else if (isSignedIn && user?.role === 'pending') {
      navigate('/select-role');
    }
  }, [isSignedIn, user, navigate]);

  // Check sign-in status for verification step
  useEffect(() => {
    if (signIn?.status === 'needs_first_factor') {
      setStep('verification');
    }
  }, [signIn?.status]);

  // Resend timer countdown
  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => setResendTimer((t) => t - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleEmailPasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!signIn) {
      toast.error('Sign in not ready. Please refresh the page.');
      return;
    }

    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }

    setLoading(true);
    try {
      // Create sign-in attempt (v6 API)
      const { error } = await signIn.create({
        identifier: email,
        password: password,
      });

      if (error) {
        console.error('Sign in error:', error);
        toast.error(error.message || 'Invalid credentials');
        setLoading(false);
        return;
      }

      if (signIn.status === 'complete') {
        // Finalize sign-in (v6 API)
        await signIn.finalize({
          navigate: () => {
            toast.success('Welcome back!');
            navigate('/select-role');
          }
        });
      } else if (signIn.status === 'needs_first_factor') {
        // Need email verification - send code
        await signIn.emailCode.sendCode();
        setStep('verification');
        setResendTimer(60);
        toast.info('Verification code sent to your email');
      } else if (signIn.status === 'needs_second_factor') {
        toast.info('Two-factor authentication required');
      } else {
        console.log('Sign in status:', signIn.status);
        toast.error('Sign-in requires additional steps');
      }
    } catch (error) {
      console.error('Login error:', error);
      const errorMessage = error.errors?.[0]?.longMessage || error.errors?.[0]?.message || error.message || 'Invalid credentials';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    if (!verificationCode) {
      toast.error('Please enter the verification code');
      return;
    }
    
    if (!signIn) {
      toast.error('Sign in session expired. Please try again.');
      navigate('/login');
      return;
    }

    setLoading(true);
    try {
      // Verify email code (v6 API)
      await signIn.emailCode.verifyCode({
        code: verificationCode,
      });

      if (signIn.status === 'complete') {
        // Finalize sign-in (v6 API)
        await signIn.finalize({
          navigate: () => {
            toast.success('Welcome back!');
            navigate('/select-role');
          }
        });
      } else {
        toast.error('Verification incomplete. Please try again.');
      }
    } catch (error) {
      console.error('Verification error:', error);
      toast.error(error.errors?.[0]?.message || error.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendTimer > 0 || !signIn) return;
    
    setLoading(true);
    try {
      await signIn.emailCode.sendCode();
      setResendTimer(60);
      toast.success('Verification code resent');
    } catch (error) {
      toast.error('Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  // Forgot Password - Send reset code
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }

    if (!signIn) {
      toast.error('Not ready. Please refresh the page.');
      return;
    }

    setLoading(true);
    try {
      // Create a sign-in attempt with reset_password_email_code strategy
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email,
      });
      
      setStep('reset-code');
      setResendTimer(60);
      toast.success('Password reset code sent to your email');
    } catch (error) {
      console.error('Forgot password error:', error);
      const errorMessage = error.errors?.[0]?.longMessage || error.errors?.[0]?.message || error.message || 'Failed to send reset code';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Verify reset code
  const handleVerifyResetCode = async (e) => {
    e.preventDefault();
    
    if (!resetCode) {
      toast.error('Please enter the reset code');
      return;
    }

    if (!signIn) {
      toast.error('Session expired. Please try again.');
      setStep('forgot');
      return;
    }

    setLoading(true);
    try {
      // Attempt first factor with the reset code
      const result = await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code: resetCode,
      });

      if (result.status === 'needs_new_password') {
        setStep('new-password');
        toast.success('Code verified! Enter your new password.');
      } else {
        console.log('Unexpected status:', result.status);
        toast.error('Please complete the password reset');
      }
    } catch (error) {
      console.error('Verify reset code error:', error);
      toast.error(error.errors?.[0]?.message || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  // Set new password
  const handleSetNewPassword = async (e) => {
    e.preventDefault();
    
    if (!newPassword || !confirmPassword) {
      toast.error('Please fill in both password fields');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    if (!signIn) {
      toast.error('Session expired. Please try again.');
      setStep('forgot');
      return;
    }

    setLoading(true);
    try {
      // Reset the password
      const result = await signIn.resetPassword({
        password: newPassword,
      });

      if (result.status === 'complete') {
        toast.success('Password reset successful! Please sign in.');
        // Reset all states
        setStep('credentials');
        setEmail('');
        setPassword('');
        setResetCode('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        console.log('Reset status:', result.status);
        toast.info('Password updated. Please sign in.');
        setStep('credentials');
      }
    } catch (error) {
      console.error('Set new password error:', error);
      toast.error(error.errors?.[0]?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  // Resend reset code
  const handleResendResetCode = async () => {
    if (resendTimer > 0 || !signIn) return;
    
    setLoading(true);
    try {
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email,
      });
      setResendTimer(60);
      toast.success('Reset code resent');
    } catch (error) {
      toast.error('Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    try {
      if (!signIn) {
        throw new Error('Sign in not initialized');
      }
      
      // Use signIn.sso for OAuth in Clerk v6+
      const { error } = await signIn.sso({
        strategy: 'oauth_google',
        redirectUrl: `${window.location.origin}/select-role`,
        redirectCallbackUrl: `${window.location.origin}/sso-callback`,
      });
      
      if (error) {
        console.error('Google sign-in error:', error);
        toast.error(error.message || 'Google sign-in failed');
        setGoogleLoading(false);
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      toast.error(error.errors?.[0]?.message || error.message || 'Google sign-in failed');
      setGoogleLoading(false);
    }
  };

  if (!userLoaded) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-red-500 flex items-center justify-center mx-auto mb-4">
            <Car className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Welcome back</h1>
          <p className="text-neutral-400 mt-2">Sign in to your account</p>
        </div>

        {step === 'credentials' && (
          <>
            {/* Google Sign-In Button */}
            <div className="mb-6">
              <button
                type="button"
                onClick={handleGoogleSignIn}
                disabled={googleLoading}
                className="w-full h-12 bg-white hover:bg-gray-100 text-gray-800 font-medium rounded-full flex items-center justify-center gap-3 transition-colors disabled:opacity-50"
              >
                {googleLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    Sign in with Google
                  </>
                )}
              </button>
            </div>

            {/* Divider */}
            <div className="relative mb-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-white/10"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-[#0a0a0a] text-neutral-500">or continue with email</span>
              </div>
            </div>

            {/* Email/Password Form */}
            <form onSubmit={handleEmailPasswordSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-white">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                  <Input 
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="pl-10 bg-[#121212] border-white/10 text-white h-12"
                    data-testid="login-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-white">Password</Label>
                  <button
                    type="button"
                    onClick={() => setStep('forgot')}
                    className="text-sm text-red-500 hover:text-red-400"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                  <Input 
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="pl-10 pr-10 bg-[#121212] border-white/10 text-white h-12"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                disabled={loading || fetchStatus === 'fetching'}
                className="w-full bg-red-500 hover:bg-red-600 h-12 rounded-full font-semibold"
                data-testid="login-submit"
              >
                {loading || fetchStatus === 'fetching' ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Sign in'}
              </Button>
            </form>
          </>
        )}

        {/* Verification Code Form */}
        {step === 'verification' && (
          <form onSubmit={handleVerificationSubmit} className="space-y-6">
            <p className="text-neutral-400 text-sm mb-4">
              A verification code has been sent to <span className="text-white">{email}</span>. 
              Enter it below to complete sign-in.
            </p>

            <div className="space-y-2">
              <Label htmlFor="code" className="text-white">Verification Code</Label>
              <Input 
                id="code"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="123456"
                className="bg-[#121212] border-white/10 text-white h-12 text-center text-lg tracking-widest"
                maxLength={6}
              />
            </div>

            <Button 
              type="submit" 
              disabled={loading || !verificationCode}
              className="w-full bg-red-500 hover:bg-red-600 h-12 rounded-full font-semibold"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify & Sign In'}
            </Button>

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  setStep('credentials');
                  setVerificationCode('');
                }}
                className="text-neutral-400 hover:text-white text-sm"
              >
                ← Back to login
              </button>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resendTimer > 0}
                className="text-red-500 hover:text-red-400 text-sm disabled:text-neutral-600"
              >
                {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend code'}
              </button>
            </div>
          </form>
        )}

        {/* Forgot Password - Enter Email */}
        {step === 'forgot' && (
          <form onSubmit={handleForgotPassword} className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-white">Reset your password</h2>
              <p className="text-neutral-400 text-sm mt-2">
                Enter your email address and we'll send you a code to reset your password.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reset-email" className="text-white">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                <Input 
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10 bg-[#121212] border-white/10 text-white h-12"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={loading || !email}
              className="w-full bg-red-500 hover:bg-red-600 h-12 rounded-full font-semibold"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Send Reset Code'}
            </Button>

            <button
              type="button"
              onClick={() => {
                setStep('credentials');
                setResetCode('');
              }}
              className="w-full text-neutral-400 hover:text-white text-sm"
            >
              ← Back to login
            </button>
          </form>
        )}

        {/* Forgot Password - Enter Reset Code */}
        {step === 'reset-code' && (
          <form onSubmit={handleVerifyResetCode} className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-white">Check your email</h2>
              <p className="text-neutral-400 text-sm mt-2">
                We sent a reset code to <span className="text-white">{email}</span>
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="reset-code" className="text-white">Reset Code</Label>
              <Input 
                id="reset-code"
                type="text"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                placeholder="123456"
                className="bg-[#121212] border-white/10 text-white h-12 text-center text-lg tracking-widest"
                maxLength={6}
              />
            </div>

            <Button 
              type="submit" 
              disabled={loading || !resetCode}
              className="w-full bg-red-500 hover:bg-red-600 h-12 rounded-full font-semibold"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Verify Code'}
            </Button>

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => {
                  setStep('forgot');
                  setResetCode('');
                }}
                className="text-neutral-400 hover:text-white text-sm"
              >
                ← Change email
              </button>
              <button
                type="button"
                onClick={handleResendResetCode}
                disabled={resendTimer > 0}
                className="text-red-500 hover:text-red-400 text-sm disabled:text-neutral-600"
              >
                {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend code'}
              </button>
            </div>
          </form>
        )}

        {/* Forgot Password - Set New Password */}
        {step === 'new-password' && (
          <form onSubmit={handleSetNewPassword} className="space-y-6">
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-white">Set new password</h2>
              <p className="text-neutral-400 text-sm mt-2">
                Create a new password for your account
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-password" className="text-white">New Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                <Input 
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10 bg-[#121212] border-white/10 text-white h-12"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 hover:text-white"
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-neutral-500">Must be at least 8 characters</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password" className="text-white">Confirm Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-500" />
                <Input 
                  id="confirm-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 bg-[#121212] border-white/10 text-white h-12"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              disabled={loading || !newPassword || !confirmPassword}
              className="w-full bg-red-500 hover:bg-red-600 h-12 rounded-full font-semibold"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Reset Password'}
            </Button>

            <button
              type="button"
              onClick={() => {
                setStep('credentials');
                setResetCode('');
                setNewPassword('');
                setConfirmPassword('');
              }}
              className="w-full text-neutral-400 hover:text-white text-sm"
            >
              ← Back to login
            </button>
          </form>
        )}

        <p className="text-center text-neutral-400 mt-8">
          Don't have an account?{' '}
          <Link to="/register" className="text-red-500 hover:text-red-400">
            Sign up
          </Link>
        </p>
      </div>
    </div>
  );
}
